import {
  EMPTY,
  interval as observableInterval,
  Observable,
  Subscription,
  throwError as observableThrowError,
  throwError,
} from 'rxjs';

import { Injectable } from '@angular/core';
import { ICashTransactionResponse } from '@models/cash-transaction.class';
import { CAPITAL_OPERATIONS } from '@models/puppet.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import {
  TRADING_USA_ORDER_STATUS,
  TRADING_USA_ORDER_UNITS,
} from '@models/trades.class';
import { TransactionsViewModel } from '@models/transaction.class';
import { Store } from '@ngrx/store';
import { OperationService } from '@services/operation.service';
import { PortfolioService } from '@services/portfolio.service';
import * as moment from 'moment-timezone';
import { catchError, first, map, take, tap } from 'rxjs/operators';
import { Configurations } from 'src/app/app.configurations';
import { AppState } from 'src/app/state/app-state';
import {
  BLOTTER_CAPITALS,
  BLOTTER_CASH,
  BLOTTER_FUNDS,
} from 'src/app/state/app.reducer';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { BlotterOrderItem } from 'src/components/trade';
import { TradeDurations } from 'src/components/trade/tradeBar';
import { NotificationService } from 'src/core/services/notification.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
import {
  getMarketName,
  MarketItem,
  SharedSolaceStreamService,
} from 'src/solace';
import { SearchService } from '..';
import { ContractsService } from './contracts.service';

@Injectable()
export class TradeService {
  orderTypeList: any[] = [
    { id: 1, name: 'Compra Normal' },
    { id: 8, name: 'Venta Normal' },
    { id: 13, name: 'Venta en Corto' },
    { id: 16, name: 'Compra Global' },
    { id: 18, name: 'Venta Global' },
    { id: 44, name: 'Compra MPL Activa' },
    { id: 45, name: 'Venta MPL Activa' },
    { id: 46, name: 'Compra MPL Pasiva' },
    { id: 47, name: 'Venta MPL Pasiva' },
    { id: 60, name: 'Compra MPL Sentido Opuesto' },
    { id: 61, name: 'Venta MPL Sentido Opuesto' },
    { id: 62, name: 'Compra MPL Volumen Oculto' },
    { id: 63, name: 'Venta MPL Volumen Oculto' },
  ];

  private algoTradingTypeList: any[] = [
    { id: 1, name: 'Static Iceberg' },
    { id: 2, name: 'Dynamic Iceberg' },
    { id: 3, name: 'Timed Iceberg' },
    { id: 4, name: 'Best Execution at Scale Market' },
    { id: 5, name: 'A Mercado' },
    { id: 6, name: 'Stop en Línea' },
    { id: 7, name: 'Stop Limitado' },
    { id: 15, name: 'Trailing Stop' },
    { id: 16, name: 'OCA' },
    { id: 20, name: 'OTA' },
  ];

  private statusList: any[] = [
    { id: 1, name: 'Pending-New' },
    { id: 2, name: 'New' },
    { id: 3, name: 'Cancel-Req' },
    { id: 4, name: 'Cancel-Pending' },
    { id: 5, name: 'Cancelled' },
    { id: 6, name: 'Cancel-Rjx' },
    { id: 7, name: 'Filled' },
    { id: 8, name: 'PtlFil' },
    { id: 9, name: 'Reject' },
    { id: 10, name: 'Ack-Gbm' },
    { id: 11, name: 'Replace-Pending' },
    { id: 12, name: 'Replaced' },
    { id: 13, name: 'Replace-Rjx' },
    { id: 14, name: 'Dk-Recv' },
    { id: 15, name: 'Trade-Bust' },
    { id: 16, name: 'Correction' },
    { id: 17, name: 'Restated' },
    { id: 20, name: 'Working' },
    { id: 21, name: 'Received' },
    { id: 22, name: 'Rjx-Gbm' },
    { id: 23, name: 'New' },
  ];

  private orderTradeTypeList: any = [
    { id: '1', name: 'Limitada', capOrderid: 1 },
    { id: '1', name: 'Limitada', capOrderid: 8 },
    { id: '2', name: 'En Línea, a mercado (MKT)', capOrderid: 4 },
    { id: '2', name: 'En Línea, a mercado (MKT)', capOrderid: 5 },
    { id: '3', name: 'MPL Activa', capOrderid: 44 },
    { id: '3', name: 'MPL Activa', capOrderid: 45 },
    { id: '4', name: 'MPL Pasiva', capOrderid: 46 },
    { id: '4', name: 'MPL Pasiva', capOrderid: 47 },
    { id: '5', name: 'MPL Volumen Oculto', capOrderid: 62 },
    { id: '5', name: 'MPL Volumen Oculto', capOrderid: 63 },
    { id: '6', name: 'One Cancel Another(OCA)', capOrderid: 16 },
    { id: '7', name: 'One Triggers Another(OTA)', capOrderid: 20 },
    { id: '8', name: 'Stop en Línea', capOrderid: 6 },
    { id: '9', name: 'Stop Limitado', capOrderid: 7 },
    { id: '10', name: 'Trailing Stop', capOrderid: 15 },
    { id: '11', name: 'Venta en Corto', capOrderid: 13 },
  ];

  private tradeDurations: any[] = TradeDurations;

  private intervalTimeOut: number = 30 * 1000;
  private intervalBlotterObservable: Observable<any> = observableInterval(
    this.intervalTimeOut,
  );

  private loggedInSubscription: Subscription;
  private intervalBlotterSubscription: Subscription;

  private isUsaContract: boolean;
  private lastUsaOrders: Array<Partial<BlotterOrderItem>>;
  private lastOrders: Array<Partial<BlotterOrderItem>>;

  constructor(
    private store: Store<AppState>,
    private operationService: OperationService,
    private portfolioService: PortfolioService,
    private notificationService: NotificationService,
    private configurations: Configurations,
    private contractsService: ContractsService,
    private searchService: SearchService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
  ) {
    this.init();
  }

  getCapitalMarketOrders(
    contractId: string,
    isTimer?: boolean,
  ): Observable<any> {
    let status: boolean;

    const stock: number[] = [0, 2];
    this.store.select('loggedIn').subscribe((value: boolean) => {
      status = value;
    });

    const observableResponse$: Observable<any> = this.getBlotterCapitalMarket(
      contractId,
      stock,
      isTimer,
    ).pipe(
      catchError((error: any) => {
        if (status) {
          this.notificationService.showServiceError(error);
        }

        return Observable.throw(error);
      }),
      tap((result: any[]) => {
        const payload = this.isUsaContract
          ? this.prepareUsaCapitalsDataBlotter(result)
          : this.prepareCapitalsDataBlotter(result);
        let didOrdersChange = false;

        if (this.isUsaContract) {
          didOrdersChange = this.checkForBlotterChanges(
            this.lastUsaOrders,
            payload,
          );
          this.lastUsaOrders = payload;

          didOrdersChange &&
            this.contractsService.getPositionSummaryBatch(undefined, true);
        } else {
          didOrdersChange = this.checkForBlotterChanges(
            this.lastOrders,
            payload,
          );
          this.lastOrders = payload;
        }

        didOrdersChange &&
          this.store.dispatch({
            type: BLOTTER_CAPITALS,
            payload,
          });
      }),
      take(1),
    );

    return observableResponse$;
  }

  getFundsOrders(contractId: string, isTimer?: boolean): Observable<any> {
    const stock: number[] = [27, 28];

    this.lastOrders = [];
    const observableResponse$: Observable<any> = this.getBlotterCapitalMarket(
      contractId,
      stock,
      isTimer,
    ).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      tap((result: any[]) => {
        this.store.dispatch({
          type: BLOTTER_FUNDS,
          payload: this.prepareFundsDataBlotter(result),
        });
      }),
      take(1),
    );

    return observableResponse$;
  }

  getCashOrders(contractId: string, isTimer?: boolean): Observable<any> {
    const stock: number[] = [-1];
    const observableResponse$: Observable<any> = this.getBlotterCapitalMarket(
      contractId,
      stock,
      isTimer,
    ).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      tap((result: any[]) => {
        this.store.dispatch({
          type: BLOTTER_CASH,
          payload: this.prepareCashDataBlotter(result),
        });
      }),
      take(1),
    );

    return observableResponse$;
  }

  cancelCapitalMarketOrder(
    orderId: string,
    predispatcher: boolean,
    validity: any,
  ): Observable<any> {
    return this.operationService
      .cancelOrder(orderId, predispatcher, validity)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  cancelUsaOrder(orderId: string): Observable<any> {
    return this.operationService.cancelUsaOrder(orderId).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);
        return throwError(error);
      }),
    );
  }

  getTransactions(info: any): Observable<any> {
    const observableResponse$: Observable<any> =
      this.portfolioService.getTransactions(info);

    observableResponse$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      take(1),
    );

    return observableResponse$;
  }

  getHistoricalCashOperations(info: any): Observable<any> {
    const observableResponse$: Observable<any> =
      this.operationService.getHistoricalCashOperations(info);

    observableResponse$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      take(1),
    );

    return observableResponse$;
  }

  getMovements(
    contractId: string,
    accountsId: string,
    pageNumber = 1,
    pageSize = 5,
    orderType = '',
    startDate = '',
    endDate = '',
  ): Observable<{
    items: ICashTransactionResponse[];
    pagination_metadata?: {
      page: number;
      page_size: number;
      page_count: number;
      total_items: number;
      previous: string;
      next: string;
    };
  }> {
    return this.operationService
      .getMovements(
        contractId,
        accountsId,
        pageNumber,
        pageSize,
        orderType,
        startDate,
        endDate,
      )
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getCashTransactionDetail(
    contractId: string,
    processData: string,
    orderId: number,
  ): Observable<any> {
    return this.operationService
      .getCashTransactionDetail(contractId, processData, orderId)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getTransferDetail(
    contractId: string,
    accountId: string,
    orderId: string,
    orderType: string,
  ): Observable<any> {
    return this.operationService
      .getTransferDetail(contractId, accountId, orderId, orderType)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  cancelTransfer(
    contractId: string,
    accountId: string,
    transferId: string,
  ): Observable<any> {
    return this.operationService
      .cancelTransfer(contractId, accountId, transferId)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getIntradayCashOperations(info: any): Observable<any> {
    const observableResponse$: Observable<any> =
      this.operationService.getIntradayCashOperations(info);

    observableResponse$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      take(1),
    );

    return observableResponse$;
  }

  getContractTopCashMovements(contractId: string): Observable<any> {
    return this.operationService.getContractTopCashMovements(contractId).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
    );
  }

  getMovementsUsa({
    startDate,
    endDate,
    page,
  }: {
    startDate: Date;
    endDate: Date;
    page: number;
  }) {
    return this.operationService
      .getUsaCapitalMovements(
        page,
        10,
        startDate.toISOString(),
        endDate.toISOString(),
      )
      .pipe(
        map((transactions) =>
          transactions
            ? TransactionsViewModel.fromJSON(transactions)
            : { transactions: [], pagination: {} },
        ),
      );
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);
    return Observable.throw(error);
  }

  private init(): void {
    this.loggedInSubscription = this.store
      .select('loggedIn')
      .subscribe((value: boolean) => {
        if (value) {
          this.intervalTimeOut =
            +this.configurations.appConfiguration.timeOuts.timeOut30Seg;
        } else {
          if (this.intervalBlotterSubscription) {
            this.intervalBlotterSubscription.unsubscribe();
          }
        }
      });

    this.store.select(selectIsUsa).subscribe((isUsa) => {
      this.isUsaContract = isUsa;
    });
  }

  private getBlotterCapitalMarket(
    contractId: string,
    stock: number[],
    isTimer: boolean,
  ): Observable<any> {
    let status: boolean;
    const date: any = moment().tz('America/Mexico_City').startOf('day');

    const data: any = {
      instrumentTypes: stock,
      processDate: date,
      accountId: contractId,
      contractId,
    };
    this.store.select('loggedIn').subscribe((value: boolean) => {
      status = value;
    });

    if (!status) {
      return EMPTY;
    }

    if (this.isUsaContract) {
      return this.operationService.getBlotterUsa(isTimer);
    }

    return this.operationService.getBlotterCapitalMarket(data, isTimer).pipe(
      map((ordersCollection: any) => {
        const getRealTimePosition = ordersCollection
          ? ordersCollection.getRealTimePosition
          : false;

        getRealTimePosition &&
          this.contractsService.getPositionSummaryBatch(
            contractId,
            getRealTimePosition,
          );
        return ordersCollection.ordersList;
      }),
    );
  }

  private prepareUsaCapitalsDataBlotter(
    data: BlotterCapitalOrder[],
  ): Array<Partial<BlotterOrderItem>> {
    return data.map((order) => {
      const foundIssue = this.searchService.issues
        ? this.searchService.findDataBy(order.instrument_id, 'instrumentId')
        : ({} as SearchBoxIssueItem);

      return {
        createdDate: order.created_date,
        hour: moment(order.created_date).format('HH:mm:ss'),
        issueId: foundIssue.issueID,
        way: order.side === CAPITAL_OPERATIONS.buy ? 'Compra' : 'Venta',
        approximatedTitles: this.getFixedOrApproximatedTitlesFromOrder(
          order,
          foundIssue,
        ),
        amount: this.getFixedOrApproximatedAmountFromOrder(order, foundIssue),
        price: order.price,
        status: order.status,
        isCancelable: order.is_cancelable,
        capitalOrderType: 'A mercado',
        // TODO: This is not correct, because we don't handle a sobID,
        // however, we rely on this id to check which order should be cancelled/cancelledAndReplaced
        // so this makes them "unique" for those handlers
        sobId: order.id,
      };
    });
  }

  private prepareCapitalsDataBlotter(data: any[]): BlotterOrderItem[] {
    let result: BlotterOrderItem[] = [];
    if (data) {
      for (const item of data) {
        let orderType: any[] = [];
        item.issueId = new ChangeCharacterFromIssue().ChangeAmpersand(
          item.issueId,
        );
        if (item.algoTradingTypeId && item.algoTradingTypeId > 0) {
          orderType = this.algoTradingTypeList.filter((o: any) => {
            return o.id === item.algoTradingTypeId;
          });
        } else {
          orderType = this.orderTypeList.filter((o: any) => {
            return o.id === item.capitalOrderTypeId;
          });
        }

        const orderTypeName: string =
          orderType.length > 0 ? orderType[0].name : '-';

        const status: any[] = this.statusList.filter((s: any) => {
          return s.id === item.gbmIntProcessStatus;
        });

        const statusName: string = status.length > 0 ? status[0].name : '-';

        const duration: any = item.duration === 0 ? 1 : item.duration;

        const durations: any[] = this.tradeDurations.filter((d: any) => {
          return d.id === duration;
        });

        const durationText: string =
          durations.length > 0 ? durations[0].name : '-';

        item.capitalOrderType = orderTypeName;

        item.hour = moment(item.processDate).format('HH:mm:ss');

        item.way = item.bitBuy ? 'Compra' : 'Venta';

        item.status = statusName;
        item.durationText = durationText;
        item.assignedQuantityPer =
          (item.assignedQuantity * 100) / item.originalQuantity / 100;

        // Se mapea el CapitalOrderType con el Tipo de Orden del Muñeco
        let orderTradeType: any[] = [];
        if (item.algoTradingTypeId && item.algoTradingTypeId > 0) {
          orderTradeType = this.orderTradeTypeList.filter((o: any) => {
            return o.capOrderid === item.algoTradingTypeId;
          });
        } else {
          orderTradeType = this.orderTradeTypeList.filter((o: any) => {
            return o.capOrderid === item.capitalOrderTypeId;
          });
        }

        const orderTypeId: string =
          orderTradeType.length > 0 ? orderTradeType[0].id : '0';
        item.orderTradeTypeId = orderTypeId;
      }

      result = data;
    }

    return result;
  }

  private prepareFundsDataBlotter(data: any[]): any[] {
    const result: any[] = [];

    if (data) {
      for (const item of data) {
        const row: any = {};

        row.fund = item.issueId;
        row.hour = moment(item.processDate).format('HH:mm:ss');
        row.settlementDate = moment(item.settlementDate).format('L');
        row.shares = item.originalQuantity;
        row.price = item.price;
        row.folio = item.sobId;
        row.way = item.bitBuy ? 'Compra' : 'Venta';
        row.total = item.originalQuantity * item.price;

        row.processDate = item.processDate;
        row.originalQuantity = item.originalQuantity;

        result.push(row);
      }
    }

    return result;
  }

  private prepareCashDataBlotter(data: any[]): any[] {
    const result: any[] = [];

    if (data) {
      for (const item of data) {
        const row: any = {};

        const status: any[] = this.statusList.filter((s: any) => {
          return s.id === item.gbmIntProcessStatus;
        });

        const statusName: string = status.length > 0 ? status[0].name : '-';

        row.hour = moment(item.processDate).format('HH:mm:ss');
        row.price = item.price;
        row.folio = item.sobId;
        row.statusName = statusName;

        row.processDate = item.processDate;

        result.push(row);
      }
    }

    return result;
  }

  private checkForBlotterChanges(
    existingOrders: Array<Partial<BlotterOrderItem>>,
    newOrders: Array<Partial<BlotterOrderItem>>,
  ): boolean {
    // When only one of the arrays is falsy
    if ((!existingOrders && !!newOrders) || (!!existingOrders && !newOrders)) {
      return true;
    }

    if (existingOrders.length !== newOrders.length) {
      return true;
    }

    const propertiesThatIndicateChanges: Array<keyof BlotterOrderItem> = [
      'assignedQuantity',
      'esStatus',
      'status',
      'isCancelable',
    ];

    for (let i = 0; i < existingOrders.length; i++) {
      if (
        propertiesThatIndicateChanges.some(
          (prop) => existingOrders[i][prop] !== newOrders[i][prop],
        )
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * This will check if the order has a fixed number of shares, and if not, it'll obtain an approx
   */
  private getFixedOrApproximatedTitlesFromOrder(
    order: BlotterCapitalOrder,
    issue: SearchBoxIssueItem,
  ): number {
    // Return early if the order has fixed amount of shares
    if (order.order_unit === TRADING_USA_ORDER_UNITS.shares) {
      return order.shares;
    }

    if (
      order.status === TRADING_USA_ORDER_STATUS.finishedTrading ||
      order.status === TRADING_USA_ORDER_STATUS.executed
    ) {
      return order.filled_quantity;
    }

    const price = this.getSidePriceFromIssue(order.side, issue);

    if (price !== undefined) {
      return order.amount / price;
    }

    return undefined;
  }

  /**
   * This will check if the order has a fixed amount, and if not, it'll obtain an approx
   */
  private getFixedOrApproximatedAmountFromOrder(
    order: BlotterCapitalOrder,
    issue: SearchBoxIssueItem,
  ): any {
    // Return early if the order has fixed amount
    if (order.order_unit === TRADING_USA_ORDER_UNITS.amount) {
      return order.amount;
    }

    const price = this.getSidePriceFromIssue(order.side, issue);
    if (price !== undefined) {
      return order.shares * price;
    }

    return undefined;
  }

  /**
   * Returns the last bid/ask price depending on the order side
   */
  private getSidePriceFromIssue(
    side: CAPITAL_OPERATIONS,
    issue: SearchBoxIssueItem,
  ) {
    const issueDetails = this.obtainIssueDetailsFromSolace(issue);
    let price: number;
    issueDetails.subscribe(({ buy, sell }) => {
      if (side === CAPITAL_OPERATIONS.buy && sell !== undefined) {
        price = sell;
        return;
      }

      if (side === CAPITAL_OPERATIONS.sell && buy !== undefined) {
        price = buy;
        return;
      }

      if (issue.lastPrice) {
        price = issue.lastPrice;
      }
    });
    return price;
  }

  /**
   * This will obtain the details of an issue using Solace
   * @param issue Issue to get the info of. Contains at least instrumentType and issueId
   */
  private obtainIssueDetailsFromSolace(
    issue: SearchBoxIssueItem,
  ): Observable<{ buy: number; sell: number }> {
    const marketObservable$ =
      this.sharedSolaceStreamService.getObservableSymbol<MarketItem>(
        getMarketName(issue.instrumentType),
        issue.issueID,
        'alerts',
      );

    return marketObservable$.pipe(
      first(),
      map((symbol: MarketItem) => {
        if (
          !symbol ||
          !symbol.posturas ||
          !symbol.posturas.body ||
          !symbol.posturas.body.length
        ) {
          return;
        }

        return {
          buy: symbol.posturas.body[0].SellPrice,
          sell: symbol.posturas.body[0].BuyPrice,
        };
      }),
    );
  }
}

interface BlotterCapitalOrder {
  id: string;
  contract_id: string;
  account_id: string;
  amount: number;
  instrument_id: string;
  order_type: string;
  side: CAPITAL_OPERATIONS;
  filled_quantity: number;
  status: string;
  price: number;
  created_date: string;
  is_cancelable: boolean;
  order_unit?: TRADING_USA_ORDER_UNITS;
  shares?: number;
}
